import { useState, useEffect, useRef } from 'react';
import { compressImageFile } from '../../utils/imageUtils';
import { formatDistance } from '../../utils/units';
import { extractServiceRecords } from '../../services/analysisUtils';
import PhotoPickerModal from '../PhotoPickerModal';

const DEV = import.meta.env.DEV;
const SERVICE_TYPES = ['DIY', 'Independent', 'Dealer'];
const MAX_PHOTOS = 10;
const SCAN_ACCEPTED_TYPES = 'image/*,.csv,.pdf,.doc,.docx,.xls,.xlsx,.txt';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCost(cost) {
  if (cost == null) return '';
  return `$${Number(cost).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/* ───────────────────── Add Service Form ───────────────────── */

function AddServiceForm({ onAdd, onCancel }) {
  const [data, setData] = useState({
    service: '',
    date: new Date().toISOString().split('T')[0],
    mileage: '',
    cost: '',
    type: 'Independent',
    notes: '',
  });
  const [photos, setPhotos] = useState([]);
  const [compressing, setCompressing] = useState(false);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const fileInputRef = useRef(null);

  async function handlePhotos(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remaining = MAX_PHOTOS - photos.length;
    const toProcess = files.slice(0, remaining);

    setCompressing(true);
    try {
      const results = await Promise.all(
        toProcess.map((file) => compressImageFile(file, 800, 0.7).catch(() => null)),
      );
      setPhotos((prev) => [...prev, ...results.filter(Boolean)].slice(0, MAX_PHOTOS));
    } finally {
      setCompressing(false);
      // Reset input so the same file can be selected again
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function removePhoto(index) {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!data.service.trim()) return;
    onAdd({
      service: data.service.trim(),
      date: data.date,
      mileage: data.mileage ? parseInt(data.mileage, 10) : null,
      cost: data.cost ? parseFloat(data.cost) : null,
      type: data.type,
      notes: data.notes.trim(),
      photos: photos.length > 0 ? photos : undefined,
      source: 'user_added',
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      autoComplete="off"
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 16,
        marginBottom: 12,
      }}
    >
      <div style={{ marginBottom: 10 }}>
        <label style={miniLabelStyle}>Service Performed</label>
        <input
          className="form-input"
          type="text"
          placeholder="e.g. Oil change, Brake pads, Timing belt"
          value={data.service}
          onChange={(e) => setData({ ...data, service: e.target.value })}
          style={miniInputStyle}
          required
          autoFocus
        />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={miniLabelStyle}>Date</label>
          <input
            className="form-input"
            type="date"
            value={data.date}
            onChange={(e) => setData({ ...data, date: e.target.value })}
            style={{ ...miniInputStyle, colorScheme: 'dark' }}
            required
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={miniLabelStyle}>Mileage</label>
          <input
            className="form-input"
            type="text"
            inputMode="numeric"
            placeholder="e.g. 25000"
            value={data.mileage}
            onChange={(e) => setData({ ...data, mileage: e.target.value })}
            style={miniInputStyle}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={miniLabelStyle}>Cost ($)</label>
          <input
            className="form-input"
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={data.cost}
            onChange={(e) => setData({ ...data, cost: e.target.value })}
            style={miniInputStyle}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={miniLabelStyle}>Done by</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {SERVICE_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setData({ ...data, type: t })}
                style={{
                  flex: 1,
                  padding: '7px 4px',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  borderRadius: 'var(--radius-sm)',
                  border: data.type === t
                    ? '1.5px solid var(--accent)'
                    : '1.5px solid var(--border)',
                  background: data.type === t
                    ? 'var(--accent-subtle)'
                    : 'var(--bg-elevated)',
                  color: data.type === t
                    ? 'var(--accent)'
                    : 'var(--text-secondary)',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={miniLabelStyle}>Notes (optional)</label>
        <input
          className="form-input"
          type="text"
          placeholder="e.g. Used OEM parts, 2yr warranty"
          value={data.notes}
          onChange={(e) => setData({ ...data, notes: e.target.value })}
          style={miniInputStyle}
        />
      </div>

      {/* Photos section */}
      <div style={{ marginBottom: 12 }}>
        <label style={miniLabelStyle}>
          Photos {photos.length > 0 ? `(${photos.length}/${MAX_PHOTOS})` : '(optional)'}
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handlePhotos}
          style={{ display: 'none' }}
        />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {photos.map((photo, i) => (
            <div key={i} style={{ position: 'relative', width: 56, height: 56 }}>
              <img
                src={photo}
                alt={`Photo ${i + 1}`}
                style={{
                  width: 56,
                  height: 56,
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)',
                }}
              />
              <button
                type="button"
                onClick={() => removePhoto(i)}
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: 'var(--accent)',
                  color: '#fff',
                  border: 'none',
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                x
              </button>
            </div>
          ))}
          {photos.length < MAX_PHOTOS && (
            <button
              type="button"
              onClick={() => setShowPhotoPicker(true)}
              disabled={compressing}
              style={{
                width: 56,
                height: 56,
                borderRadius: 'var(--radius-sm)',
                border: '1.5px dashed var(--border)',
                background: 'var(--bg-elevated)',
                color: 'var(--text-muted)',
                cursor: compressing ? 'not-allowed' : 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                opacity: compressing ? 0.5 : 1,
              }}
            >
              {compressing ? (
                <span style={{ fontSize: '0.5rem' }}>...</span>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span style={{ fontSize: '0.5rem', fontWeight: 600 }}>Add</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            flex: 1,
            padding: '8px 12px',
            fontSize: '0.8125rem',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!data.service.trim()}
          style={{
            flex: 1,
            padding: '8px 12px',
            fontSize: '0.8125rem',
            fontWeight: 600,
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: 'var(--accent)',
            color: 'white',
            cursor: 'pointer',
            opacity: data.service.trim() ? 1 : 0.5,
          }}
        >
          Add Entry
        </button>
      </div>

      <PhotoPickerModal
        show={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onSelect={(source) => {
          setShowPhotoPicker(false);
          fileInputRef.current.removeAttribute('capture');
          fileInputRef.current.click();
        }}
      />
    </form>
  );
}

/* ───────────────────── Edit Service Form (inline) ───────────────────── */

function EditServiceForm({ entry, onSave, onCancel }) {
  const [data, setData] = useState({
    service: entry.service || '',
    date: entry.date || '',
    mileage: entry.mileage != null ? String(entry.mileage) : '',
    cost: entry.cost != null ? String(entry.cost) : '',
    type: entry.type || 'Independent',
    notes: entry.notes || '',
  });

  function handleSubmit(e) {
    e.preventDefault();
    if (!data.service.trim()) return;
    onSave(entry.id, {
      service: data.service.trim(),
      date: data.date,
      mileage: data.mileage ? parseInt(data.mileage, 10) : null,
      cost: data.cost ? parseFloat(data.cost) : null,
      type: data.type,
      notes: data.notes.trim(),
    });
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 12 }}>
      <div style={{ marginBottom: 8 }}>
        <label style={miniLabelStyle}>Service</label>
        <input
          className="form-input"
          type="text"
          value={data.service}
          onChange={(e) => setData({ ...data, service: e.target.value })}
          style={miniInputStyle}
          required
          autoFocus
        />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={miniLabelStyle}>Date</label>
          <input
            className="form-input"
            type="date"
            value={data.date}
            onChange={(e) => setData({ ...data, date: e.target.value })}
            style={{ ...miniInputStyle, colorScheme: 'dark' }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={miniLabelStyle}>Mileage</label>
          <input
            className="form-input"
            type="text"
            inputMode="numeric"
            value={data.mileage}
            onChange={(e) => setData({ ...data, mileage: e.target.value })}
            style={miniInputStyle}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={miniLabelStyle}>Cost ($)</label>
          <input
            className="form-input"
            type="text"
            inputMode="decimal"
            value={data.cost}
            onChange={(e) => setData({ ...data, cost: e.target.value })}
            style={miniInputStyle}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={miniLabelStyle}>Done by</label>
          <div style={{ display: 'flex', gap: 4 }}>
            {SERVICE_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setData({ ...data, type: t })}
                style={{
                  flex: 1,
                  padding: '6px 2px',
                  fontSize: '0.625rem',
                  fontWeight: 600,
                  borderRadius: 'var(--radius-sm)',
                  border: data.type === t ? '1.5px solid var(--accent)' : '1.5px solid var(--border)',
                  background: data.type === t ? 'var(--accent-subtle)' : 'var(--bg-elevated)',
                  color: data.type === t ? 'var(--accent)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={miniLabelStyle}>Notes</label>
        <input
          className="form-input"
          type="text"
          value={data.notes}
          onChange={(e) => setData({ ...data, notes: e.target.value })}
          style={miniInputStyle}
        />
      </div>

      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            flex: 1,
            padding: '7px 10px',
            fontSize: '0.75rem',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!data.service.trim()}
          style={{
            flex: 1,
            padding: '7px 10px',
            fontSize: '0.75rem',
            fontWeight: 600,
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: 'var(--accent)',
            color: 'white',
            cursor: 'pointer',
            opacity: data.service.trim() ? 1 : 0.5,
          }}
        >
          Save Changes
        </button>
      </div>
    </form>
  );
}

/* ───────────────────── Service entry row (interactive) ───────────────────── */

function ServiceEntry({ entry, onUpdate, onRemove, canLog, distanceUnits }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const confirmTimerRef = useRef(null);

  // Auto-reset confirm delete after 3 seconds
  useEffect(() => {
    if (confirmDelete) {
      confirmTimerRef.current = setTimeout(() => setConfirmDelete(false), 3000);
      return () => clearTimeout(confirmTimerRef.current);
    }
  }, [confirmDelete]);

  function handleToggle() {
    if (editing) return; // don't collapse while editing
    setExpanded((prev) => !prev);
    setConfirmDelete(false);
  }

  function handleEdit(e) {
    e.stopPropagation();
    setEditing(true);
  }

  function handleSaveEdit(id, fields) {
    onUpdate?.(id, fields);
    setEditing(false);
    setExpanded(false);
  }

  function handleCancelEdit() {
    setEditing(false);
  }

  function handleDelete(e) {
    e.stopPropagation();
    if (confirmDelete) {
      onRemove?.(entry.id);
    } else {
      setConfirmDelete(true);
    }
  }

  return (
    <div
      style={{
        padding: '12px 0',
        borderBottom: '1px solid var(--border)',
        cursor: editing ? 'default' : 'pointer',
      }}
      onClick={handleToggle}
    >
      <div style={{ display: 'flex', gap: 12 }}>
        {/* Left dot / timeline */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: entry.source === 'ai_recommendation' ? 'var(--accent)' : 'var(--success, #22c55e)',
              flexShrink: 0,
            }}
          />
          <div
            style={{
              width: 1,
              flex: 1,
              background: 'var(--border)',
              marginTop: 4,
            }}
          />
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
              {entry.service}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              {entry.cost != null && (
                <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--accent)' }}>
                  {formatCost(entry.cost)}
                </span>
              )}
              {/* Chevron */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--text-muted)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  transition: 'transform 0.2s',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {formatDate(entry.date)}
            </span>
            {entry.mileage && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {formatDistance(entry.mileage, distanceUnits)}
              </span>
            )}
            <span
              style={{
                fontSize: '0.6875rem',
                fontWeight: 600,
                padding: '1px 6px',
                borderRadius: 4,
                background: 'var(--bg-elevated)',
                color: 'var(--text-muted)',
              }}
            >
              {entry.type}
            </span>
            {entry.source === 'extracted_from_upload' && (
              <span
                style={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  padding: '1px 6px',
                  borderRadius: 4,
                  background: 'rgba(59, 130, 246, 0.15)',
                  color: '#60a5fa',
                }}
              >
                Scanned
              </span>
            )}
          </div>

          {/* Collapsed: show notes preview */}
          {!expanded && entry.notes && (
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {entry.notes}
            </p>
          )}

          {/* Expanded content */}
          {expanded && !editing && (
            <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
              {entry.notes && (
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.4 }}>
                  {entry.notes}
                </p>
              )}

              {entry.shopName && (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                  Shop: {entry.shopName}
                </p>
              )}

              {/* Photo thumbnails */}
              {entry.photos?.length > 0 && (
                <div style={{
                  display: 'flex',
                  gap: 6,
                  marginTop: 8,
                  marginBottom: 8,
                  overflowX: 'auto',
                  paddingBottom: 4,
                }}>
                  {entry.photos.map((photo, i) => (
                    <img
                      key={i}
                      src={photo}
                      alt={`Service photo ${i + 1}`}
                      style={{
                        width: 48,
                        height: 48,
                        objectFit: 'cover',
                        borderRadius: 4,
                        border: '1px solid var(--border)',
                        flexShrink: 0,
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Action buttons — only shown with LOG_ACCESS+ */}
              {canLog !== false && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button
                    onClick={handleEdit}
                    style={{
                      flex: 1,
                      padding: '7px 10px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                      background: 'transparent',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={handleDelete}
                    style={{
                      flex: 1,
                      padding: '7px 10px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: 'var(--radius-sm)',
                      border: confirmDelete ? 'none' : '1px solid rgba(239, 68, 68, 0.3)',
                      background: confirmDelete ? 'rgba(239, 68, 68, 0.9)' : 'transparent',
                      color: confirmDelete ? '#fff' : '#ef4444',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      transition: 'all 0.2s',
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                    {confirmDelete ? 'Confirm Delete' : 'Delete'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Edit form */}
          {expanded && editing && (
            <div onClick={(e) => e.stopPropagation()}>
              <EditServiceForm
                entry={entry}
                onSave={handleSaveEdit}
                onCancel={handleCancelEdit}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────── Main component ───────────────────── */

export default function ServiceHistory({ entries, vehicle, onAddEntry, onUpdateEntry, onRemoveEntry, onToast, forceShowAdd, onCancelAdd, canLog }) {
  const [showAdd, setShowAdd] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scanFileRef = useRef(null);
  const scanAbortRef = useRef(null);
  const addFormRef = useRef(null);

  // Open add form when parent triggers it, then scroll into view
  useEffect(() => {
    if (forceShowAdd) {
      setShowAdd(true);
      // Wait for form to render, then scroll to it
      setTimeout(() => {
        addFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [forceShowAdd]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (scanAbortRef.current) scanAbortRef.current.abort();
    };
  }, []);

  const totalCost = entries.reduce((sum, e) => sum + (e.cost || 0), 0);

  // Sort entries: most recent first
  const sortedEntries = [...entries].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  function handleAdd(entry) {
    onAddEntry(entry);
    setShowAdd(false);
    onCancelAdd?.();
  }

  function handleCancelAdd() {
    setShowAdd(false);
    onCancelAdd?.();
  }

  // ─── Scan Document ───

  async function handleScanFiles(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (scanFileRef.current) scanFileRef.current.value = '';

    setScanning(true);
    const controller = new AbortController();
    scanAbortRef.current = controller;

    try {
      // Read files into the format extractServiceRecords expects
      const fileData = [];
      for (const file of files) {
        const dataUrl = await readFileAsDataURL(file);
        const isImage = file.type.startsWith('image/');
        if (isImage) {
          fileData.push({ type: 'image', name: file.name, data: dataUrl });
        } else {
          fileData.push({ type: 'file', name: file.name, mimeType: file.type, data: dataUrl });
        }
      }

      if (DEV) console.log('[ServiceHistory] Scanning', fileData.length, 'files for service records');

      const extractedEntries = await extractServiceRecords(fileData, vehicle, controller.signal);

      if (extractedEntries.length > 0) {
        if (DEV) console.log('[ServiceHistory] Extracted', extractedEntries.length, 'entries');
        for (const e of extractedEntries) {
          onAddEntry({
            service: e.service || 'Unknown Service',
            date: e.date || null,
            mileage: e.mileage || null,
            cost: e.cost || null,
            type: e.type || 'Independent',
            shopName: e.shopName || null,
            notes: e.notes || '',
            source: 'extracted_from_upload',
          });
        }
        onToast?.(`Extracted ${extractedEntries.length} service ${extractedEntries.length === 1 ? 'entry' : 'entries'}`);
      } else {
        onToast?.('No service entries found in the uploaded files');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[ServiceHistory] Scan failed:', err.message);
      onToast?.('Failed to scan documents. Please try again.');
    } finally {
      setScanning(false);
      scanAbortRef.current = null;
    }
  }

  function handleExportCSV() {
    if (sortedEntries.length === 0) return;
    const vehicleLabel = vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : 'Vehicle';
    const headers = ['Date', 'Service', 'Cost', 'Mileage', 'Type', 'Notes'];
    const rows = sortedEntries.map((e) => [
      e.date || '',
      `"${(e.service || '').replace(/"/g, '""')}"`,
      e.cost != null ? e.cost : '',
      e.mileage || '',
      e.type || '',
      `"${(e.notes || '').replace(/"/g, '""')}"`,
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${vehicleLabel.replace(/\s+/g, '_')}_service_history.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2
          style={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Service History
        </h2>
      </div>

      {/* Hidden file input for scanning */}
      <input
        ref={scanFileRef}
        type="file"
        accept={SCAN_ACCEPTED_TYPES}
        multiple
        onChange={handleScanFiles}
        style={{ display: 'none' }}
      />

      {/* Scanning overlay */}
      {scanning && (
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--accent)',
            borderRadius: 'var(--radius-md)',
            padding: '20px 16px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
          }}
        >
          <div style={{
            width: 20, height: 20, borderRadius: '50%',
            border: '2.5px solid var(--accent)', borderTopColor: 'transparent',
            animation: 'spin 1s linear infinite',
          }} />
          <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: 500 }}>
            Scanning documents for service records...
          </span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Action buttons: Add Entry + Scan Document + Export CSV — gated by canLog */}
      {!showAdd && !scanning && canLog !== false && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button
            onClick={() => setShowAdd(true)}
            style={{
              flex: 1,
              padding: '10px 12px',
              fontSize: '0.8125rem',
              fontWeight: 600,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'var(--accent)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Entry
          </button>
          <button
            onClick={() => {
              if (scanFileRef.current) {
                scanFileRef.current.value = '';
                scanFileRef.current.click();
              }
            }}
            style={{
              flex: 1,
              padding: '10px 12px',
              fontSize: '0.8125rem',
              fontWeight: 600,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--accent)',
              background: 'transparent',
              color: 'var(--accent)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Scan Document
          </button>
          {/* CSV export hidden — opens preview on iPad with no way to save */}
        </div>
      )}

      {showAdd && (
        <div ref={addFormRef}>
          <AddServiceForm onAdd={handleAdd} onCancel={handleCancelAdd} />
        </div>
      )}

      {sortedEntries.length === 0 && !showAdd ? (
        <div
          className="card"
          style={{
            textAlign: 'center',
            padding: '24px 20px',
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
          }}
        >
          <p style={{ marginBottom: 8 }}>No services logged yet.</p>
          <p style={{ fontSize: '0.8125rem' }}>
            Mark maintenance items complete or tap "Add Entry" to start tracking.
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: '4px 16px' }}>
          {sortedEntries.map((entry) => (
            <ServiceEntry
              key={entry.id}
              entry={entry}
              onUpdate={onUpdateEntry}
              onRemove={onRemoveEntry}
              canLog={canLog}
              distanceUnits={vehicle?.distance_units}
            />
          ))}

          {/* Total cost summary */}
          {sortedEntries.length > 0 && totalCost > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0 8px',
              }}
            >
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                Total Spent
              </span>
              <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--accent)' }}>
                {formatCost(totalCost)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

const miniLabelStyle = {
  display: 'block',
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 4,
};

const miniInputStyle = {
  fontSize: '0.8125rem',
  padding: '8px 10px',
};
